// src/settings/components/macro/macro-import-dialog.tsx
//
// DEV-ONLY "Import phrases" (spec §6.13): paste the phrase library JSON, validate every definition
// (`phrase-import.ts`), and CREATE each through the existing CreateUpdateMacro service — shared
// org-wide (`isGlobal`), because the library's "shared" phrases carry no collaborator list and
// `isGlobal` is the only sharing that service can express without one. Never overwrites, never
// deletes: a key the owner already has is reported as "exists".
//
// Mounted by MacroTab only when `isDevHost()` AND the tab's own add-macro condition holds; on any
// other host it is not rendered at all. The macro EDITOR does not know pieces yet, so this is how
// piece-carrying phrases reach dev tonight — and editing one of them in the editor would drop its
// pieces (Quill strips attributes it has not registered), so leave imported phrases alone there.

import { useState } from 'react';
import { Button, Modal, TextArea } from '@scripsteam/scrips-signal-ds';
import { useMacrosByProviderId } from '@/settings/services';
import { useSaveMacro } from '@/settings/services/use-save-macro';
import { parsePhraseLibrary, planPhraseImport, type ImportPlan } from './phrase-import';

export interface MacroImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  organizationId: string;
}

interface Outcome {
  imported: Array<{ macroKey: string; warnings: string[] }>;
  skipped: Array<{ macroKey: string; reason: string }>;
}

export function MacroImportDialog({ open, onOpenChange, providerId, organizationId }: MacroImportDialogProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const existing = useMacrosByProviderId(providerId, organizationId, '');
  const save = useSaveMacro(providerId);

  const run = async () => {
    setError(null);
    setOutcome(null);
    const parsed = parsePhraseLibrary(text);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    if (!existing.data) {
      setError('Your existing macros have not loaded yet, so existing keys cannot be checked. Try again in a moment.');
      return;
    }
    const ownKeys = existing.data.filter((m) => m.ownerId === providerId && !m.isDeleted).map((m) => m.macroKey);
    const plan: ImportPlan = planPhraseImport(parsed.items, ownKeys, { ownerId: providerId, organizationId });
    const result: Outcome = { imported: [], skipped: [...plan.skipped] };
    setRunning(true);
    // One at a time, so a failure is attributable to its phrase and nothing races the server.
    for (const item of plan.toSave) {
      try {
        await save.mutateAsync(item.body);
        result.imported.push({ macroKey: item.macroKey, warnings: item.warnings });
      } catch (e) {
        result.skipped.push({ macroKey: item.macroKey, reason: `save failed: ${e instanceof Error ? e.message : String(e)}` });
      }
    }
    setRunning(false);
    setOutcome(result);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Import phrases (dev only)"
      size="xl"
      footer={
        <div className="flex items-center gap-2">
          <Button type="button" onClick={run} disabled={!text.trim() || running}>
            {running ? 'Importing…' : 'Import'}
          </Button>
          <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-text-secondary">
          Paste the phrase library JSON (an array of phrase definitions, or the library file). Each phrase is created as a
          new shared macro owned by this provider. Existing keys are skipped, never overwritten.
        </p>
        <TextArea
          label="Phrase library JSON"
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
        />
        {error && (
          <p role="alert" className="m-0 text-sm text-status-error">
            {error}
          </p>
        )}
        {outcome && (
          <div role="status" className="flex flex-col gap-2 text-sm">
            <p className="m-0 font-semibold text-text-primary">
              Imported {outcome.imported.length} · skipped {outcome.skipped.length}
            </p>
            {outcome.imported.length > 0 && (
              <ul aria-label="Imported" className="m-0 flex list-none flex-col gap-1 p-0">
                {outcome.imported.map((i) => (
                  <li key={i.macroKey}>
                    <span className="font-medium">{i.macroKey}</span>
                    {i.warnings.map((w) => (
                      <span key={w} className="block text-xs text-status-amber-text">
                        Warning: {w}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            )}
            {outcome.skipped.length > 0 && (
              <ul aria-label="Skipped" className="m-0 flex list-none flex-col gap-1 p-0">
                {outcome.skipped.map((s, idx) => (
                  <li key={`${s.macroKey}-${idx}`}>
                    <span className="font-medium">{s.macroKey}</span> — {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
